import React, { useEffect, useState } from 'react'
import Table from "react-bootstrap/Table";
import { Modal } from 'react-bootstrap';
import CustomPagination from '../components/CustomPagination';
import DeleteModal from '../components/DeleteModal';
import Loader from '../components/Loader';
import { deleteReportedStory, editStory, getReportedStories, refreshReportedCount } from '../controller';
import { requestHandler } from '../helper';
import { formatDateAsItIs } from '../helper/common';
import { imagePath } from '../helper/image';
import { toast } from 'react-toastify';

const fullUrl = (p) => (p ? (/^https?:\/\//.test(p) ? p : `${imagePath()}${p.replace(/^\/+/, '')}`) : '');
const fullName = (u) => [u?.firstName, u?.lastName].filter(Boolean).join(' ') || u?.userName || '-';

// "Flagged Stories" tab of Reported Content. Same table design as Flagged Activity: one row per report.
function ReportedStories({ limit = 10 }) {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [confirm, setConfirm] = useState(null); // { action: 'approve' | 'reject' | 'delete', id }
  const [paginationValue, setPaginationValue] = useState({ page: 1, totalData: 0, totalPages: 0 });
  const { page, totalData, totalPages } = paginationValue;
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalData);

  const getStories = async (pageNo = page) => {
    await requestHandler(
      async () => await getReportedStories(limit, pageNo),
      setLoading,
      (res) => {
        const { data, page, totalData, totalPages } = res.data;
        setStories(data);
        setPaginationValue({ page, totalData, totalPages });
      }
    );
  };

  useEffect(() => {
    getStories(page);
    // eslint-disable-next-line
  }, [page, limit]);

  const askConfirm = (action, id) => setConfirm({ action, id });

  const handleConfirm = async () => {
    const { action, id } = confirm;
    await requestHandler(
      async () => await (action === 'delete' ? deleteReportedStory(id) : editStory({ status: action === 'approve' ? 1 : 2 }, id)),
      setLoading,
      (res) => {
        toast.success(res.message);
        refreshReportedCount();
        setConfirm(null);
        // step back a page when the last story on it was removed
        if (action === 'delete' && stories.length === 1 && page > 1) {
          setPaginationValue((prev) => ({ ...prev, page: page - 1 }));
        } else {
          getStories(page);
        }
      }
    );
  };

  return (
    <>
      {loading && <Loader />}
      <div className="overflow-x-auto theme-scrollbar">
        <Table className="theme-table">
          <thead>
            <tr>
              <th>Post By</th>
              <th>Story Type</th>
              <th>Post Date</th>
              <th>Reported By</th>
              <th>Flagged Reason</th>
              <th>Reported Date</th>
              <th>Media</th>
              <th>Status</th>
              <th width="200" className='text-center'>Action</th>
            </tr>
          </thead>
          <tbody>
            {stories.length > 0 ? (
              stories.flatMap((row) => (row.reportId || []).map((report, index) => (
                <tr key={`${row._id}-${index}`}>
                  <td>{fullName(row.userId)}</td>
                  <td>{row.mediaType === 'video' ? 'Video' : 'Photo'}</td>
                  <td>{formatDateAsItIs(row.createdAt)}</td>
                  <td>{fullName(report?.senderId)}</td>
                  <td>{report?.reason || "-"}</td>
                  <td>{formatDateAsItIs(report?.createdAt)}</td>
                  <td>
                    <div style={{ cursor: 'pointer' }} onClick={() => setViewing(row)}>
                      <img
                        src={fullUrl(row.mediaType === 'video' ? row.thumbnailUrl : row.mediaUrl)}
                        alt="Story"
                        style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd' }}
                      />
                    </div>
                  </td>
                  <td>
                    <ul className="table-status">
                      <li className={row.status === 1 ? 'status-success' : 'status-danger'}>
                        {row.status === 1 ? 'Approved' : row.status === 2 ? 'Rejected' : 'Pending'}
                      </li>
                    </ul>
                  </td>
                  <td width={220}>
                    <div className='d-flex gap-3 justify-content-center align-items-center'>
                      {!row.status && (
                        <>
                          <button className="btn btn-light-danger" onClick={() => askConfirm('reject', row._id)}>
                            <img src="/images/svg/table-false.svg" alt="icon" />
                          </button>
                          <button className="btn btn-light-success" onClick={() => askConfirm('approve', row._id)}>
                            <img src="/images/svg/table-true.svg" alt="icon" />
                          </button>
                        </>
                      )}
                      {row.status > 0 && (
                        <button className="btn btn-light-danger" onClick={() => askConfirm('delete', row._id)}>
                          <img src="/images/svg/table-delete.svg" alt="icon" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )))
            ) : (
              <tr>
                <td colSpan={9} className="text-center p-5 fs-5">
                  No Records Found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mt-1">
        <p className="mb-0 fs-16 fw-500 text-light">
          Showing {end === 0 ? 0 : start} to {end} of {totalData} entries
        </p>
        {totalPages > 1 && (
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={(p) => setPaginationValue({ ...paginationValue, page: p })}
          />
        )}
      </div>

      <Modal show={!!viewing} onHide={() => setViewing(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Story</Modal.Title>
        </Modal.Header>
        {viewing && (
          <Modal.Body className="text-center">
            {viewing.mediaType === 'video'
              ? <video style={{ maxWidth: '100%', maxHeight: '70vh' }} src={fullUrl(viewing.mediaUrl)} poster={fullUrl(viewing.thumbnailUrl)} controls />
              : <img style={{ maxWidth: '100%', maxHeight: '70vh' }} src={fullUrl(viewing.mediaUrl)} alt="Story" />}
          </Modal.Body>
        )}
      </Modal>

      <DeleteModal
        show={!!confirm}
        handleClose={() => setConfirm(null)}
        handleDelete={handleConfirm}
        title={confirm?.action === 'delete' ? 'Confirm Delete' : 'Confirmation'}
        message={confirm?.action === 'delete'
          ? 'Are you sure you want to delete this story? It will be removed for everyone and cannot be restored.'
          : `Are you sure you want to ${confirm?.action} this story?`}
      />
    </>
  )
}

export default ReportedStories
